// Libs
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import qs from 'qs';

// Config
import { getConfig } from '@app/config';
import { handleAuthError } from '@app/slice/authRedirect';

// baseQuery riêng cho báo cáo: đọc runtime config (đổi API URL không cần rebuild)
// và gắn Bearer token của Owner (claim HotelID → multi-tenant per-DB).
// Vỏ bọc ApiResponse<T> = { success, data, message, errors, pagination }.
const reportsBaseQuery = async (args, api, extraOptions) => {
  const config = getConfig();

  if (!config) {
    return { error: { status: 500, data: 'Config can not loaded yet' } };
  }

  const rawBaseQuery = fetchBaseQuery({
    // Reports dùng cùng gốc API v1; tách biến để có thể trỏ khác khi cần.
    baseUrl:
      config.REACT_APP_REPORTS_API_URL || config.REACT_APP_OWNER_API_URL || '',
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('ownerToken');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
    validateStatus: (response, body) =>
      response.status === 200 && body?.success !== false,
    paramsSerializer: (params) => qs.stringify(params),
  });

  const result = await rawBaseQuery(args, api, extraOptions);
  // 401 ở API báo cáo → xoá token, về màn login (403 OwnerAreaForbidden là bình thường).
  return handleAuthError(result, args);
};

// 6 endpoint báo cáo — HỢP ĐỒNG THẬT DiCloud Star4 (base REACT_APP_REPORTS_API_URL,
// :5002/api/v1). Vỏ ApiResponse<T> (camelCase, ISO date, số VND); transformResponse
// bóc vỏ .data. Tham số ngày là fromDate/toDate (KHÔNG phải from/to).
export const reportsApiSlice = createApi({
  reducerPath: 'reportsApi',
  baseQuery: reportsBaseQuery,
  tagTypes: ['Report'],
  // Cache FE 10 PHÚT (mặc định RTK chỉ 60s → rời màn >1 phút quay lại là spinner
  // dù backend cache 10' trả ~ms — DEV báo 22/08). 600s khớp đúng TTL cache backend
  // của nhóm báo cáo: trong 10' quay lại màn nào cũng hiện NGAY từ cache.
  keepUnusedDataFor: 600,
  endpoints: (builder) => ({
    // BC1) Doanh thu theo thời gian — GET /reports/revenue?fromDate&toDate&groupBy=day
    // → dòng theo ngày { date, transactionCount, netAmount, taxAmount,
    //   serviceChargeAmount, grossAmount }. NET = GiaGoc + PhiPV (đã gồm phí phục vụ,
    //   chưa gồm VAT).
    getRevenueReport: builder.query({
      query: ({ fromDate, toDate, groupBy = 'day' } = {}) => ({
        url: '/reports/revenue',
        params: { fromDate, toDate, groupBy },
      }),
      transformResponse: (resp) => resp?.data ?? resp,
      providesTags: ['Report'],
    }),
    // BC2) Cơ cấu doanh thu — GET /reports/revenue-breakdown?fromDate&toDate&groupBy=
    //   segment|channel (endpoint RIÊNG, không phải /reports/revenue). groupBy=channel
    //   có lát "Khách vãng lai" (walk-in). → [{ segment|channel, amount }].
    getRevenueBreakdown: builder.query({
      query: ({ fromDate, toDate, groupBy = 'segment' } = {}) => ({
        url: '/reports/revenue-breakdown',
        params: { fromDate, toDate, groupBy },
      }),
      transformResponse: (resp) => resp?.data ?? resp,
      providesTags: ['Report'],
    }),
    // BC3) Công suất & loại phòng — ĐÃ CHỐT alias room-availability của DiCloud.
    // GET /room-availability/stats?fromDate&toDate[&roomTypeId] → ApiResponse<T>,
    //   data = { fromDate, toDate, stats: [...] }. Mỗi phần tử stats[] = 1 ngày × 1
    //   loại phòng { date, roomTypeID, roomTypeName, totalRooms, occupied, available,
    //   ooo, occupancyPct, earlyBird, bbc, al, ral, oos }. FE tự tổng hợp (rollup) —
    //   xem OccupancyReport.jsx: sum occupied/totalRooms/ooo rồi
    //   occ% = occupied / (totalRooms − ooo) × 100, KHÔNG avg field %.
    //   `oos` là field THÔNG TIN (bảng dbo.PhongOOS, tập RỜI với PhongOOO) —
    //   KHÔNG trừ vào mẫu số, để khớp hero KPI và DiHotel native (quyết định 29/07).
    getOccupancyReport: builder.query({
      query: ({ fromDate, toDate, roomTypeId } = {}) => ({
        url: '/room-availability/stats',
        params: { fromDate, toDate, ...(roomTypeId ? { roomTypeId } : {}) },
      }),
      transformResponse: (resp) => resp?.data ?? resp,
      providesTags: ['Report'],
    }),
    // BC4) Dự báo — GET /reports/forecast?horizon=30|60|90.
    getForecastReport: builder.query({
      query: ({ horizon = 30 } = {}) => ({
        url: '/reports/forecast',
        params: { horizon },
      }),
      transformResponse: (resp) => resp?.data ?? resp,
      providesTags: ['Report'],
    }),
    // BC5) Top công ty/đại lý — GET /reports/agency?fromDate&toDate&topN (topN mặc định 10).
    getAgencyReport: builder.query({
      query: ({ fromDate, toDate, topN = 10 } = {}) => ({
        url: '/reports/agency',
        params: { fromDate, toDate, topN },
      }),
      transformResponse: (resp) => resp?.data ?? resp,
      providesTags: ['Report'],
    }),
    // BC6) So sánh kỳ — GET /reports/compare?period=month|year&refDate=<optional>.
    //   BE tự tính cùng kỳ năm trước; FE KHÔNG truyền 4 ngày.
    getCompareReport: builder.query({
      query: ({ period = 'month', refDate } = {}) => ({
        url: '/reports/compare',
        params: { period, ...(refDate ? { refDate } : {}) },
      }),
      transformResponse: (resp) => resp?.data ?? resp,
      providesTags: ['Report'],
    }),
  }),
});

export const {
  useGetRevenueReportQuery,
  useGetRevenueBreakdownQuery,
  useGetOccupancyReportQuery,
  useGetForecastReportQuery,
  useGetAgencyReportQuery,
  useGetCompareReportQuery,
} = reportsApiSlice;
